import type { CSSProperties, ReactElement, ReactNode } from 'react'
import { CONTACT_TYPE_OPTIONS, type ChoiceOption, type FieldDef, type FieldType } from '../schema/intakeSchema'
import { ExpandableTextarea } from './ExpandableTextarea'
import { FieldHelp, LinesTextarea } from './FormFields'
import { PriorityBadge } from './PriorityBadge'
import { safeHttpUrl } from '../utils/safeUrl'

type Value = unknown
type SetValue = (v: Value) => void

/** Coerce a captured value to a string[] without crashing. Imports / seed data
 *  occasionally arrive as a single string ("Sales, Marketing") where the schema
 *  expects an array; this normalizes both shapes. */
function toStringArray(v: Value, sep: string | RegExp = ','): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string')
  if (typeof v === 'string')
    return v
      .split(sep)
      .map((s) => s.trim())
      .filter(Boolean)
  return []
}

/** Coerce a captured value to an array of objects without crashing. */
function toObjectArray<T extends object>(v: Value): T[] {
  if (Array.isArray(v)) return v.filter((x): x is T => typeof x === 'object' && x !== null)
  return []
}

interface Props {
  field: FieldDef
  value: Value
  onChange: SetValue
  meta?:
    | { source?: string; url?: string | null | undefined; actor?: string | null | undefined }
    | undefined
}

export function FieldRenderer({ field, value, onChange, meta }: Props) {
  // One muted helper line: who last touched the field (FieldMeta.actor,
  // stamped server-side) and/or where a prefill came from.
  const sourceHref = safeHttpUrl(meta?.url)
  const helper =
    meta?.url || meta?.actor ? (
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
        {meta.actor && <>Last edited by {meta.actor}</>}
        {meta.actor && meta.url && ' · '}
        {meta.url && (
          <>
            Prefilled from {meta.source}{' '}
            {sourceHref ? (
              <a href={sourceHref} target="_blank" rel="noopener noreferrer">
                (source)
              </a>
            ) : (
              <>({meta.url})</>
            )}
          </>
        )}
      </div>
    ) : null

  return (
    <div data-testid={`field-${field.key}`} style={{ marginBottom: '1.6rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', fontWeight: 600, marginBottom: '0.45rem' }}>
        <span>{field.label}</span>
        <PriorityBadge priority={field.priority} />
        <FieldHelp label={field.label} hint={field.hint} help={field.help} />
      </label>
      <FieldBody field={field} value={value} onChange={onChange} />
      {helper}
    </div>
  )
}

/** Props every kind renderer receives; `type` is narrowed to that kind's shape. */
interface KindProps<K extends FieldType['kind']> {
  field: FieldDef
  type: Extract<FieldType, { kind: K }>
  value: Value
  onChange: SetValue
}

type KindRenderers = { [K in FieldType['kind']]: (props: KindProps<K>) => ReactElement }

/** One renderer per schema field kind. Adding a kind to `FieldType` without an
 *  entry here is a compile error. */
const KIND_RENDERERS: KindRenderers = {
  text: ({ type, value, onChange }) => (
    <input
      type="text"
      value={(value as string) ?? ''}
      placeholder={type.placeholder}
      spellCheck
      onChange={(e) => onChange(e.target.value)}
    />
  ),
  textarea: ({ field, type, value, onChange }) => (
    <ExpandableTextarea
      label={field.label}
      value={(value as string) ?? ''}
      // The schema's `rows` are semantic sizes (2 = short, 3 = paragraph),
      // doubled at render time so there's room to type.
      rows={(type.rows ?? 3) * 2}
      placeholder={type.placeholder}
      onChange={(v) => onChange(v)}
    />
  ),
  number: ({ type, value, onChange }) => (
    <input
      type="number"
      value={(value as number) ?? ''}
      placeholder={type.placeholder}
      min={type.min}
      max={type.max}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  ),
  radio: ({ field, type, value, onChange }) => (
    <RadioGroup name={field.key} options={type.options} value={value as string} onChange={onChange} />
  ),
  multiselect: ({ type, value, onChange }) => (
    <MultiSelect
      options={type.options}
      allowOther={!!type.allowOther}
      value={toStringArray(value, ',')}
      onChange={onChange}
    />
  ),
  select: ({ field, type, value, onChange }) => (
    <OptionSelect
      value={value as string | undefined}
      options={type.options}
      blankLabel={type.placeholder ?? '—'}
      testId={`select-${field.key}`}
      onChange={(v) => onChange(v || null)}
    />
  ),
  list: ({ type, value, onChange }) => (
    <LinesTextarea
      label="List"
      value={toStringArray(value, /\r?\n/)}
      placeholder={type.placeholder ?? 'One per line'}
      onChange={onChange}
    />
  ),
  'eligibility-grid': ({ type, value, onChange }) => (
    <EligibilityGrid rows={type.rows} value={value} onChange={onChange} />
  ),
  'use-cases-table': ({ value, onChange }) => (
    <UseCasesTable value={toObjectArray<UseCaseRow>(value)} onChange={onChange} />
  ),
  'qa-list': ({ value, onChange }) => <QAList value={toObjectArray<QARow>(value)} onChange={onChange} />,
  'contacts-table': ({ value, onChange }) => (
    <ContactsTable value={toObjectArray<ContactRow>(value)} onChange={onChange} />
  ),
}

function FieldBody({ field, value, onChange }: { field: FieldDef; value: Value; onChange: SetValue }) {
  // Stored schemas / imports can carry a kind this build doesn't know.
  const kind = field.type.kind
  if (!Object.hasOwn(KIND_RENDERERS, kind)) return <div>Unknown field type</div>
  const Render = KIND_RENDERERS[kind] as (props: KindProps<typeof kind>) => ReactElement
  return <Render field={field} type={field.type} value={value} onChange={onChange} />
}

/** A `<select>` with a leading blank option; emits the raw value ('' = blank). */
function OptionSelect({
  value,
  options,
  blankLabel = '—',
  testId,
  onChange,
}: {
  value: string | undefined
  options: ChoiceOption[]
  blankLabel?: string
  testId?: string
  onChange: (v: string) => void
}) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{blankLabel}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/** Row edits for the repeating-row kinds: every edit emits the full row list. */
function rowEditor<T extends object>(rows: T[], onChange: SetValue) {
  return {
    update: (i: number, patch: Partial<T>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))),
    add: () => onChange([...rows, {}]),
    remove: (i: number) => onChange(rows.filter((_, j) => j !== i)),
  }
}

function AddRowButton({ label, testId, onClick }: { label: string; testId: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ marginTop: '0.5rem' }} data-testid={testId}>
      {label}
    </button>
  )
}

const ROW_BORDER: CSSProperties = { borderBottom: '1px solid var(--border)' }

/** Bordered full-width table: one header row of `[label, style]` cells over `children` rows. */
function GridTable({ head, children }: { head: Array<[string, CSSProperties]>; children: ReactNode }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
      <thead>
        <tr style={ROW_BORDER}>
          {head.map(([label, style], i) => (
            <th key={i} style={style}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

/** One row of the company contact roster (company_profile.contacts). */
interface ContactRow {
  name?: string | undefined
  title?: string | undefined
  contact_type?: string | undefined
  email?: string | undefined
  phone?: string | undefined
}

const CONTACT_CELL_LABEL: CSSProperties = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.2rem' }

function ContactCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span style={CONTACT_CELL_LABEL}>{label}</span>
      {children}
    </label>
  )
}

function ContactsTable({ value, onChange }: { value: ContactRow[]; onChange: SetValue }) {
  const { update, add, remove } = rowEditor(value, onChange)
  return (
    <div>
      {value.map((r, i) => (
        // Two-line card per contact: who they are, then how to reach them.
        <div
          key={i}
          data-testid={`contact-card-${i}`}
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: '0.5rem' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Contact {i + 1}</span>
            <button type="button" onClick={() => remove(i)} aria-label="remove contact" data-testid={`contact-remove-${i}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <ContactCell label="Name">
              <input type="text" value={r.name ?? ''} spellCheck data-testid={`contact-name-${i}`} onChange={(e) => update(i, { name: e.target.value })} />
            </ContactCell>
            <ContactCell label="Title">
              <input type="text" value={r.title ?? ''} spellCheck placeholder="CEO, VP Eng…" onChange={(e) => update(i, { title: e.target.value })} />
            </ContactCell>
            <ContactCell label="Type">
              <OptionSelect
                value={r.contact_type}
                options={CONTACT_TYPE_OPTIONS}
                testId={`contact-type-${i}`}
                onChange={(v) => update(i, { contact_type: v || undefined })}
              />
            </ContactCell>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr', gap: '0.5rem' }}>
            <ContactCell label="Email">
              <input type="email" value={r.email ?? ''} data-testid={`contact-email-${i}`} onChange={(e) => update(i, { email: e.target.value })} />
            </ContactCell>
            <ContactCell label="Phone">
              <input type="tel" value={r.phone ?? ''} onChange={(e) => update(i, { phone: e.target.value })} />
            </ContactCell>
          </div>
        </div>
      ))}
      <AddRowButton label="+ Add contact" testId="add-contact-row" onClick={add} />
    </div>
  )
}

function RadioGroup({ name, options, value, onChange }: { name: string; options: ChoiceOption[]; value?: string | undefined; onChange: SetValue }) {
  // Long option lists (Industry has 12) lay out in a responsive multi-column
  // grid so they read at a glance; short lists keep the single column.
  const many = options.length > 6
  return (
    <div className={`radio-group${many ? ' radio-group--grid' : ''}`}>
      {options.map((o) => {
        const checked = value === o.value
        return (
          <label key={o.value} className={`radio-option${checked ? ' radio-option--checked' : ''}`}>
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={checked}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        )
      })}
    </div>
  )
}

function MultiSelect({ options, value, allowOther, onChange }: { options: ChoiceOption[]; value: string[]; allowOther: boolean; onChange: SetValue }) {
  const selected = new Set(value)
  const known = new Set(options.map((o) => o.value))
  const otherText = value.filter((v) => !known.has(v)).join(', ')

  const toggle = (v: string) => {
    const next = new Set(selected)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    onChange(Array.from(next))
  }

  // While typing, keep the raw split — including a partial trailing entry
  // ("Foo, Bar, Bazz<cursor>") — so spaces and commas survive the controlled
  // re-render; trimming and empty-filtering only run on blur.
  const setOther = (raw: string, finalize: boolean) => {
    const extras = raw.split(',')
    onChange([
      ...value.filter((v) => known.has(v)),
      ...(finalize ? extras.map((s) => s.trim()).filter(Boolean) : extras),
    ])
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.25rem' }}>
        {options.map((o) => (
          <label key={o.value} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {allowOther && (
        <input
          type="text"
          placeholder="Other (comma-separated)"
          value={otherText}
          spellCheck
          onChange={(e) => setOther(e.target.value, false)}
          onBlur={(e) => setOther(e.target.value, true)}
          style={{ marginTop: '0.5rem' }}
        />
      )}
    </div>
  )
}

type EligibilityRow = { result?: 'Pass' | 'Fail' | undefined; notes?: string | undefined }
type EligibilityValue = Record<string, EligibilityRow>

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

/** Only the `{row: {result, notes}}` entries of a stored value. A string that
 *  landed in this field (an old clarification answer overwrote it) must not be
 *  spread into the saved object one character per key — and any such junk
 *  already stored is dropped on the next save. */
function eligibilityRows(value: unknown): EligibilityValue {
  if (!isObject(value)) return {}
  return Object.fromEntries(Object.entries(value).filter(([, row]) => isObject(row))) as EligibilityValue
}

function EligibilityGrid({ rows, value, onChange }: { rows: ChoiceOption[]; value?: unknown; onChange: SetValue }) {
  const v = eligibilityRows(value)
  const update = (key: string, patch: Partial<EligibilityRow>) => {
    onChange({ ...v, [key]: { ...v[key], ...patch } })
  }
  return (
    <GridTable
      head={[
        ['Check', { textAlign: 'left', padding: '0.4rem' }],
        ['Pass', { width: '4rem', padding: '0.4rem', textAlign: 'center' }],
        ['Notes', { textAlign: 'left', padding: '0.4rem' }],
      ]}
    >
      {rows.map((r) => {
        const row = v[r.value] ?? {}
        return (
          <tr key={r.value} style={ROW_BORDER}>
            <td style={{ padding: '0.4rem', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{r.label}</span>
                <FieldHelp label={r.label} hint={r.hint} help={r.help} />
              </div>
            </td>
            <td style={{ padding: '0.4rem', verticalAlign: 'top', textAlign: 'center' }}>
              {/* Checked = this rule passes. Unchecked = not satisfied (a
                  disqualifier); downstream only ever counts result === 'Pass'. */}
              <input
                type="checkbox"
                checked={row.result === 'Pass'}
                onChange={(e) => update(r.value, { result: e.target.checked ? 'Pass' : undefined })}
                data-testid={`elig-pass-${r.value}`}
                aria-label={`${r.label} — passes`}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
            </td>
            <td style={{ padding: '0.4rem' }}>
              <input
                type="text"
                value={row.notes ?? ''}
                spellCheck
                onChange={(e) => update(r.value, { notes: e.target.value })}
              />
            </td>
          </tr>
        )
      })}
    </GridTable>
  )
}

interface QARow {
  question?: string | undefined
  answer?: string | undefined
}

function QAList({ value, onChange }: { value: QARow[]; onChange: SetValue }) {
  const { update, add, remove } = rowEditor(value, onChange)
  const cell: CSSProperties = { padding: '0.3rem', verticalAlign: 'top' }
  return (
    <div>
      <GridTable
        head={[
          ['Question', { textAlign: 'left', padding: '0.4rem', width: '40%' }],
          ['Answer', { textAlign: 'left', padding: '0.4rem' }],
          ['', { width: '3rem' }],
        ]}
      >
        {value.map((r, i) => (
          <tr key={i} style={ROW_BORDER}>
            {(['question', 'answer'] as const).map((k) => (
              <td key={k} style={cell}>
                <ExpandableTextarea
                  label={`${k === 'question' ? 'Question' : 'Answer'} ${i + 1}`}
                  value={r[k] ?? ''}
                  rows={2}
                  onChange={(v) => update(i, { [k]: v })}
                  testId={`qa-${k}-${i}`}
                />
              </td>
            ))}
            <td style={{ padding: '0.3rem', textAlign: 'center', verticalAlign: 'top' }}>
              <button type="button" onClick={() => remove(i)} aria-label="remove Q&A row" data-testid={`qa-remove-${i}`}>
                ✕
              </button>
            </td>
          </tr>
        ))}
      </GridTable>
      <AddRowButton label="+ Add Q&A" testId="add-qa-row" onClick={add} />
    </div>
  )
}

interface UseCaseRow {
  name?: string | undefined
  description?: string | undefined
  type?: 'New' | 'Modernization' | 'Migration' | undefined
  impact?: 'H' | 'M' | 'L' | undefined
}

const USE_CASE_TYPE_OPTIONS: ChoiceOption[] = ['New', 'Modernization', 'Migration'].map((v) => ({ value: v, label: v }))
const USE_CASE_IMPACT_OPTIONS: ChoiceOption[] = [
  { value: 'H', label: 'High' },
  { value: 'M', label: 'Medium' },
  { value: 'L', label: 'Low' },
]

function UseCasesTable({ value, onChange }: { value: UseCaseRow[]; onChange: SetValue }) {
  const { update, add, remove } = rowEditor(value, onChange)
  const cell: CSSProperties = { padding: '0.3rem' }
  return (
    <div>
      <GridTable
        head={[
          ['Name', { textAlign: 'left', padding: '0.4rem' }],
          ['Description', { textAlign: 'left', padding: '0.4rem' }],
          ['Type', { width: '9rem', padding: '0.4rem' }],
          ['Impact', { width: '6rem', padding: '0.4rem' }],
          ['', { width: '3rem' }],
        ]}
      >
        {value.map((r, i) => (
          <tr key={i} style={ROW_BORDER}>
            <td style={cell}>
              <input type="text" value={r.name ?? ''} spellCheck onChange={(e) => update(i, { name: e.target.value })} />
            </td>
            <td style={cell}>
              <input type="text" value={r.description ?? ''} spellCheck onChange={(e) => update(i, { description: e.target.value })} />
            </td>
            <td style={cell}>
              <OptionSelect
                value={r.type}
                options={USE_CASE_TYPE_OPTIONS}
                onChange={(v) => update(i, { type: (v || undefined) as UseCaseRow['type'] })}
              />
            </td>
            <td style={cell}>
              <OptionSelect
                value={r.impact}
                options={USE_CASE_IMPACT_OPTIONS}
                onChange={(v) => update(i, { impact: (v || undefined) as UseCaseRow['impact'] })}
              />
            </td>
            <td style={{ ...cell, textAlign: 'center' }}>
              <button type="button" onClick={() => remove(i)} aria-label="remove row">✕</button>
            </td>
          </tr>
        ))}
      </GridTable>
      <AddRowButton label="+ Add row" testId="add-use-case-row" onClick={add} />
    </div>
  )
}
